import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import type {QueryPersister} from '@github-ui/react-query'
import type {ShouldRevalidateFunctionArgs} from '@github-ui/react-router'
import {COMMENTS_VIEW_MODE_SEARCH_PARAM} from '../helpers/constants'

/**
 * `PullRequestsController#show` decides between Conversation and Overview on each request (hard & soft)
 * by checking the user's feature enrollment. If the enrollment changed since the route first mounted, a soft-nav refetch
 * can return a different payload shape than the route rendered with and break the page.
 *
 * This function pins the `overview` param on every request to the route's initial shape, so a soft-nav refetch
 * from a tab that already mounted one shape can come back with the other tab's shape.
 *
 * This query param was originally created to prevent race conditions and pin the user to/from overview when
 * toggling the feature state. We're re-using that capability to do that same for the Conversation vs Overview index route swap.
 **/
export function withOverviewParam(pathname: string, overview: boolean) {
  return `${pathname}?overview=${overview}`
}

export function shouldRevalidatePullRequestPageData({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
  formMethod,
}: ShouldRevalidateFunctionArgs) {
  const commentViewChanged =
    currentUrl.searchParams.get(COMMENTS_VIEW_MODE_SEARCH_PARAM) !==
    nextUrl.searchParams.get(COMMENTS_VIEW_MODE_SEARCH_PARAM)
  const currentSearchParams = new URLSearchParams(currentUrl.searchParams)
  const nextSearchParams = new URLSearchParams(nextUrl.searchParams)
  currentSearchParams.delete(COMMENTS_VIEW_MODE_SEARCH_PARAM)
  nextSearchParams.delete(COMMENTS_VIEW_MODE_SEARCH_PARAM)

  if (
    formMethod === undefined &&
    commentViewChanged &&
    currentUrl.pathname === nextUrl.pathname &&
    currentSearchParams.toString() === nextSearchParams.toString()
  ) {
    return false
  }

  return defaultShouldRevalidate
}

/**
 * Shorthand for creating a query config that fetches from a pull request's `page_data` endpoint.
 * Builds the pathname as `/:owner/:repo/pull/:pr_number/page_data/:name`.
 *
 * By default the response is fetched with `queryFnFetch`. Pass `options.queryFn`
 * to supply a custom fetcher — e.g. one that decodes a Protobuf response and/or
 * normalizes a legacy payload to the canonical shape. The custom `queryFn` is
 * responsible for any schema validation/decoding it needs.
 */
export function pageDataQuery<Res, Name extends string>(
  name: Name,
  enabled: boolean | (() => boolean) = true,
  options?: {
    persister?: QueryPersister
    queryFn?: (queryKey: Parameters<typeof queryFnFetch>[0]) => Promise<Res>
  },
) {
  return {
    queryName: name,
    queryDeps: ({params}: {params: {owner: string; repo: string; pr_number: string}}) =>
      pageDataQueryDeps({params, name}),
    queryFn: options?.queryFn ?? (async (queryKey: Parameters<typeof queryFnFetch>[0]) => queryFnFetch<Res>(queryKey)),
    enabled,
    ...(options?.persister ? {persister: options.persister} : {}),
  }
}

export function pageDataQueryDeps({
  params,
  name,
}: {
  params: {owner: string; repo: string; pr_number: string}
  name: string
}) {
  return {
    pathname: `/${params.owner}/${params.repo}/pull/${params.pr_number}/page_data/${name}`,
  }
}
